import { Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import type { NewCustomer, UpdateCustomer, StripeCustomerEntity } from '@vy/types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { PaymentProviderSelect } from '../../../../../../components/design/selects/PaymentProviderSelect';
import { useActivatedIntegration } from '../../../../../../hooks/use-activated-integration';
import { vyFormRegister } from '../../../../../../utils/react-hook-forms-utils';
import { StripeCustomersDropdown } from '../../../../customers-component/StripeCustomersDropdown';
import { PaddleCustomersDropdown } from '../../../../customers-component/PaddleCustomersDropdown';
import type { CreateCustomerMode } from '../../../create-customer-page-types/CreateCustomerModeType';
import { getSelectorAttribute, createCustomerPageSelectors } from '@vy/app-selectors';
import { findIntegrationByProviderInEntities } from '@vy/services';
import { VyIcon } from '@vy/ui-design-system';
import { colors } from '../../../../../../assets/themes/colors.ts';
import { VyFormSection } from '../../../../../../components/design/layout';

export const CustomerPaymentConfiguration: React.FC<CreateCustomerMode> = ({ mode: _mode = 'create' }) => {
  const { getValues, register, setValue, formState, control } = useFormContext<
    NewCustomer | UpdateCustomer
  >();

  const isStripeEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.stripe);
  const isPaddleEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.paddle);

  const integrationEntities = useWatch({
    control,
    name: 'integrationEntities',
  });

  const externalIntegration = useWatch({
    control,
    name: 'externalIntegration',
  });

  const stripeEntity = findIntegrationByProviderInEntities<StripeCustomerEntity>(
    integrationEntities ?? [],
    INTEGRATION_PROVIDERS.stripe,
  );
  const hasStripePaymentMethod = isNotNullOrUndefined(stripeEntity);

  // Paddle links the customer via `externalIntegration` (the registry-backed field),
  // not the legacy `integrationEntities` that Stripe uses.
  const hasPaddlePaymentMethod = (externalIntegration ?? []).some(
    (integration) => integration.type === INTEGRATION_PROVIDERS.paddle,
  );

  // Both Stripe and Paddle can auto-collect (charge) an invoice on export.
  const canAutoCollectInvoice = hasStripePaymentMethod || hasPaddlePaymentMethod;

  return (
    <VyFormSection
      title="Payments"
      icon={
        <VyIcon
          icon="MoneyDollarCircleOutline"
          height={18}
          width={18}
          viewBox="0 0 18 18"
          color={colors.grey[90]}
        />
      }
    >
      <Grid container rowSpacing={3} columnSpacing={2} size={12}>
        {isStripeEnabled && (
          <Grid size={3}>
            <StripeCustomersDropdown showDropdown={true} label="Stripe Payment Provider" />
          </Grid>
        )}
        {isPaddleEnabled && (
          <Grid size={3}>
            <PaddleCustomersDropdown label="Paddle Payment Provider" />
          </Grid>
        )}
        {!isStripeEnabled && !isPaddleEnabled && (
          <Grid size={3}>
            <Controller
              name="settings.paymentProvider"
              render={({ field }) => (
                <PaymentProviderSelect
                  {...getSelectorAttribute(createCustomerPageSelectors.configuration.primaryPaymentProvider)}
                  label="Primary Payment Provider"
                  {...field}
                  fullWidth
                  error={isNotNullOrUndefined(formState.errors.settings?.paymentProvider)}
                  helperText={formState.errors.settings?.paymentProvider?.message}
                />
              )}
            />
          </Grid>
        )}
        <Grid size={3}>
          <Controller
            name="settings.secondaryPaymentProvider"
            render={({ field }) => (
              <PaymentProviderSelect
                {...getSelectorAttribute(createCustomerPageSelectors.configuration.secondaryPaymentProvider)}
                label="2nd Payment Provider"
                {...field}
                fullWidth
                error={isNotNullOrUndefined(formState.errors.settings?.secondaryPaymentProvider)}
                helperText={formState.errors.settings?.secondaryPaymentProvider?.message}
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <TextField
            {...getSelectorAttribute(createCustomerPageSelectors.configuration.wallet)}
            label="Wallet"
            {...vyFormRegister(register, 'settings.wallet')}
            defaultValue={getValues('settings.wallet')}
            fullWidth
            error={isNotNullOrUndefined(formState.errors.settings?.wallet)}
            helperText={formState.errors.settings?.wallet?.message}
          />
        </Grid>
        <Grid size={3}>
          <FormControlLabel
            control={
              <Checkbox
                disabled={!canAutoCollectInvoice}
                {...vyFormRegister(register, 'settings.payments.autoCollectInvoice')}
                {...getSelectorAttribute(createCustomerPageSelectors.configuration.autoCollectInvoice)}
                defaultChecked={getValues('settings.payments.autoCollectInvoice')}
                onChange={(_, checked) =>
                  setValue('settings.payments.autoCollectInvoice', checked, {
                    shouldDirty: true,
                  })
                }
              />
            }
            sx={{
              alignContent: 'center',
              height: '100%',
              paddingTop: '30px',
            }}
            label="Auto-charge payment"
          />
        </Grid>
      </Grid>
    </VyFormSection>
  );
};
